/**
 * 企业用户域 API（后缀 /merchant/api/v1/corporate/*，corporate-api §4.1~§4.7）。
 *
 * S9 合同交付 1：ONB 域封装 + 通用写助手 corporateWrite（If-Match/X-Confirm 头映射，
 * 供顺延轮 PAGE-010~014 复用）。DTO 与 corporate-api §4.1 逐字段对齐；
 * BIGINT 一律 string（MerchantPortalJacksonConfig：Long/long 出站全量 ToString）。
 * 幂等：写请求 Idempotency-Key 由 client.ts 恒注入，opts.idempotencyKey 显式复用同键。
 *
 * V7.3 T1 补全：MBR-001 聚合详情 / APR / BUD / BIL / EXP / RSK 六域封装。
 * 纯函数（查询构建/头映射/错误分类/状态文案）在 ./corporateCore（node 可直测）。
 */
import {
  merchantDelete,
  merchantGet,
  merchantPatch,
  merchantPost,
  merchantPut,
  merchantUpload,
  type ApiResult,
} from './client';
import {
  buildApprovalsQuery,
  buildBillsQuery,
  buildBudgetsQuery,
  buildCardsQuery,
  buildCorporateWriteHeaders,
  buildCustomersQuery,
  buildExpensesQuery,
  buildMembersQuery,
  buildOnboardingsQuery,
  buildRiskRulesQuery,
  type CorporateApprovalListQuery,
  type CorporateBillListQuery,
  type CorporateBudgetListQuery,
  type CorporateCardListQuery,
  type CorporateCustomerListQuery,
  type CorporateExpenseListQuery,
  type CorporateMemberListQuery,
  type CorporateOnboardingListQuery,
  type CorporateRiskRuleListQuery,
  type CorporateWriteHeaderOpts,
} from './corporateCore';

export * from './corporateCore';

// ================= DTO（对齐后端 record / corporate-api §4.1） =================

/** ONB-001 列表项（CorporateOnboardingItemView；regNo 仅掩码，禁完整注册号）。 */
export type CorporateOnboardingItem = {
  applicationId: string;
  applicationNo: string;
  corpName: string;
  regNoMask: string | null;
  /** COMPLETE / INCOMPLETE */
  docStatus: string;
  /** 后端实况 NONE / FILING / FILED（契约口径 NOT_FILED/FILING/FILED/ESCALATED，映射函数双收敛） */
  platformStatus: string | null;
  /** OnboardingStatus 枚举名 */
  status: string;
  submittedAt: string | null;
  /** 待补字段清单（非待补件态为空列表） */
  resubmitFields: string[] | null;
};

/** ONB-001 分页出参（total: long → string；page/pageSize: int → number）。 */
type CorporateOnboardingListData = {
  total: string;
  items: CorporateOnboardingItem[];
  page: number;
  pageSize: number;
};

/** UBO 穿透声明（持股比例 decimal string）。 */
type CorporateUboItem = {
  name: string;
  idNoMask: string;
  shareRatio: string | null;
  controlFlag: boolean;
};

/** 审核时间线（WORM 投影）。 */
type CorporateTimelineItem = {
  status: string;
  at: string | null;
  operatorType: string;
  remark: string | null;
};

/** ONB-001 详情（列表字段基础上增加 §4.1 详情字段；影像仅私有 objectKey）。 */
export type CorporateOnboardingDetail = CorporateOnboardingItem & {
  legalName: string | null;
  /** 完整注册号（水印留痕场景，后端返回；页面禁渲染，仅 regNoMask 展示） */
  registrationNo: string | null;
  jurisdiction: string | null;
  legalPersonName: string | null;
  legalPersonIdType: string | null;
  /** 营业执照私有对象键 */
  licenseFileId: string | null;
  /** 企业授权书私有对象键 */
  authorizationFileId: string | null;
  uboDocument: CorporateUboItem[] | null;
  legalPersonKycCaseId: string | null;
  contactKycCaseId: string | null;
  needPlatformReview: boolean;
  rejectReasonPublic: string | null;
  timeline: CorporateTimelineItem[] | null;
};

/** ONB-002 决策结果（APPROVE 时附 corpId/corpNo）。 */
type CorporateDecisionData = {
  applicationId: string;
  status: string;
  corpId?: string | null;
  corpNo?: string | null;
};

/** ONB-002 决策 body（decision 四态；REJECT/SUPPLEMENT reason 必填 1-500 由页面校验）。 */
export type CorporateDecisionBody = {
  decision: 'APPROVE' | 'REJECT' | 'SUPPLEMENT' | 'ESCALATE';
  reason?: string;
};

/** ONB-006 企业客户总览项（CorporateCustomerItemView；计数 long → string）。 */
export type CorporateCustomerItem = {
  corpId: string;
  corpNo: string;
  corpName: string;
  regNoMask: string | null;
  /** KybStatus 枚举名 NONE/VALID/INCOMPLETE/REJECTED */
  kybStatus: string | null;
  memberCount: string;
  cardCount: string;
  createdAt: string | null;
};

/** ONB-006 分页出参。 */
type CorporateCustomerListData = {
  total: string;
  items: CorporateCustomerItem[];
  page: number;
  pageSize: number;
};

// ================= 通用写助手 =================

type CorporateWriteOpts = CorporateWriteHeaderOpts & {
  /** 显式幂等键：同一逻辑写请求（含 401 refresh 重放）必须复用同键；缺省由 client 生成 */
  idempotencyKey?: string;
};

/** USE_MOCK 恒 false，mock 回调仅满足既有签名；误调用即显式失败。 */
const unsupportedMock = (): never => {
  throw new Error('mock_unsupported: USE_MOCK=false');
};

/**
 * 企业域通用写助手（POST/PUT/DELETE 统一入口）：
 * Idempotency-Key 恒注入（client 兜底）+ opts.idempotencyKey 复用同键；
 * ifMatch → If-Match、confirm → X-Confirm: true（corporate-api BUD-003 / RSK-002 口径）。
 */
async function corporateWrite<T>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body: unknown,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<T>> {
  const requestOpts = {
    idempotencyKey: opts.idempotencyKey,
    headers: buildCorporateWriteHeaders(opts),
  };
  if (method === 'POST') {
    return merchantPost<T>(path, body, unsupportedMock, requestOpts);
  }
  if (method === 'PUT') {
    return merchantPut<T>(path, body, unsupportedMock, requestOpts);
  }
  return merchantDelete<T>(path, unsupportedMock, requestOpts);
}

// ================= ONB 域 API =================

/** ONB-001 开户申请列表（默认 status 由页面传 PENDING_APPROVAL；后端亦兜底同默认）。 */
export async function listCorporateOnboardings(
  query: CorporateOnboardingListQuery = {},
): Promise<ApiResult<CorporateOnboardingListData>> {
  const qs = buildOnboardingsQuery(query);
  return merchantGet<CorporateOnboardingListData>(
    `/corporate/onboardings${qs ? `?${qs}` : ''}`,
    () => ({ total: '0', items: [], page: 1, pageSize: 20 }),
  );
}

/** ONB-001 开户申请详情（含 UBO/时间线/影像 objectKey）。 */
export async function getCorporateOnboarding(
  id: string,
): Promise<ApiResult<CorporateOnboardingDetail>> {
  return merchantGet<CorporateOnboardingDetail>(
    `/corporate/onboardings/${encodeURIComponent(id)}`,
    unsupportedMock,
  );
}

/** ONB-002 审核决策（Idempotency-Key 必填；4517 高风险 / 4518 KYC 未通过 / 4502 状态机非法）。 */
export async function decideCorporateOnboarding(
  id: string,
  body: CorporateDecisionBody,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateDecisionData>> {
  return corporateWrite<CorporateDecisionData>(
    'POST',
    `/corporate/onboardings/${encodeURIComponent(id)}/decision`,
    body,
    opts,
  );
}

/** ONB-005 平台备案重试（仅 APPROVED/FILING 且无回执可重试；失败 4514 retryable 不改本地开通）。 */
export async function retryCorporateFiling(
  id: string,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateDecisionData>> {
  return corporateWrite<CorporateDecisionData>(
    'POST',
    `/corporate/onboardings/${encodeURIComponent(id)}/file-to-platform`,
    undefined,
    opts,
  );
}

/** ONB-006 企业客户列表（PAGE-009；keyword/kybStatus/from/to 全量服务端过滤，from/to 为开通时间 YYYY-MM-DD 闭区间）。 */
export async function listCorporateCustomers(
  query: CorporateCustomerListQuery = {},
): Promise<ApiResult<CorporateCustomerListData>> {
  const qs = buildCustomersQuery(query);
  return merchantGet<CorporateCustomerListData>(
    `/corporate${qs ? `?${qs}` : ''}`,
    () => ({ total: '0', items: [], page: 1, pageSize: 20 }),
  );
}

// ================= MBR 域（MBR-001 聚合详情；PAGE-010） =================

/** MBR-001 部门树节点（parentId 根部门为 null；≤5 级）。 */
type CorporateDepartmentNode = {
  id: string;
  name: string;
  parentId: string | null;
  depth: number;
  managerMemberId: string | null;
};

/** MBR-001 成员行（无联系方式明文；cardCount long → number）。 */
type CorporateDetailMemberRow = {
  memberId: string;
  fullName: string;
  departmentId: string | null;
  /** ADMIN / FINANCE / EMPLOYEE */
  role: string | null;
  status: string | null;
  cardCount: number;
};

/** MBR-001 卡概要（card.status 投影：total/active/frozen）。 */
type CorporateCardSummary = {
  total: number;
  active: number;
  frozen: number;
};

/** MBR-001 预算概要分类行。 */
type CorporateBudgetCategoryRow = {
  budgetId: string;
  budgetType: string | null;
  subjectId: string | null;
  subjectKey: string | null;
  amount: string | null;
  usedAmount: string | null;
  currency: string | null;
  status: string | null;
};

/** MBR-001 预算概要。 */
type CorporateBudgetSummary = {
  totalAmount: string | null;
  usedAmount: string | null;
  categories: CorporateBudgetCategoryRow[];
};

/** MBR-001 企业详情聚合（CorporateDetailView 逐字段对齐）。 */
export type CorporateDetail = {
  corpId: string;
  corpNo: string;
  corpName: string;
  /** NONE / VALID / INCOMPLETE / REJECTED */
  kybStatus: string | null;
  /** NONE / FILING / FILED */
  platformStatus: string | null;
  departmentTree: CorporateDepartmentNode[];
  members: CorporateDetailMemberRow[];
  cardSummary: CorporateCardSummary;
  budgetSummary: CorporateBudgetSummary;
};

/** MBR-001 企业详情聚合（EMPLOYEE 注入时后端返回本人可见子集；商户侧 read 权限全量）。 */
export async function getCorporateDetail(
  corpId: string,
): Promise<ApiResult<CorporateDetail>> {
  return merchantGet<CorporateDetail>(
    `/corporate/${encodeURIComponent(corpId)}`,
    () => {
      throw new Error('mock_unsupported: USE_MOCK=false');
    },
  );
}

// ================= APR 域（APR-001/002；PAGE-011） =================

/** APR-001 授权摘要快照（tx_summary 冻结事实）。 */
type CorporateApprovalTxSummary = {
  amount: string | null;
  currency: string | null;
  mcc: string | null;
  merchantName: string | null;
  occurredAt: string | null;
  channelTxId: string | null;
};

/** APR-001 触发规则快照。 */
type CorporateApprovalTriggerRule = {
  ruleSource: string | null;
  singleThreshold: string | null;
  triggeredBy: string | null;
};

/** APR-001 审批单列表项（CorporateApprovalItemView 逐字段对齐）。 */
export type CorporateApprovalItem = {
  approvalId: string;
  approvalNo: string;
  cardId: string;
  panMask: string | null;
  memberName: string | null;
  txSummary: CorporateApprovalTxSummary | null;
  /** decimal string */
  amount: string | null;
  currency: string | null;
  triggerRule: CorporateApprovalTriggerRule | null;
  /** PENDING_APPROVAL / APPROVED / REJECTED / EXPIRED / CANCELLED */
  status: string | null;
  submittedAt: string | null;
  expiresAt: string | null;
};

/** APR-001 分页出参。 */
type CorporateApprovalListData = {
  total: string;
  items: CorporateApprovalItem[];
  page: number;
  pageSize: number;
};

/** APR-002 决策 body（REJECT reason 必填 1-500 由页面校验）。 */
type CorporateApprovalDecisionBody = {
  decision: 'APPROVE' | 'REJECT';
  reason?: string;
};

/** APR-002 决策结果（APPROVE 时 replayStatus SUCCESS/FAILED；REJECT 时 null）。 */
type CorporateApprovalDecisionData = {
  approvalId: string;
  approvalNo: string;
  status: string | null;
  replayStatus: string | null;
  replayErrorCode: string | null;
};

/** APR-001 审批单列表（status 缺省由页面传 PENDING_APPROVAL）。 */
export async function listCorporateApprovals(
  corpId: string,
  query: CorporateApprovalListQuery = {},
): Promise<ApiResult<CorporateApprovalListData>> {
  const qs = buildApprovalsQuery(query);
  return merchantGet<CorporateApprovalListData>(
    `/corporate/${encodeURIComponent(corpId)}/approvals${qs ? `?${qs}` : ''}`,
    () => ({ total: '0', items: [], page: 1, pageSize: 20 }),
  );
}

/** APR-002 审批决策（Idempotency-Key 恒注入；APPROVE 同步重放授权）。 */
export async function decideApproval(
  approvalId: string,
  body: CorporateApprovalDecisionBody,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateApprovalDecisionData>> {
  return corporateWrite<CorporateApprovalDecisionData>(
    'POST',
    `/corporate/approvals/${encodeURIComponent(approvalId)}/decision`,
    body,
    opts,
  );
}

// ================= BUD 域（BUD-001/002/003；PAGE-012） =================

/** BUD-001 预算列表项（CorporateBudgetItemView 十三字段全集；version 乐观锁）。 */
export type CorporateBudgetItem = {
  budgetId: string;
  parentBudgetId: string | null;
  /** TOTAL / DEPARTMENT / PROJECT / CHANNEL_ACCOUNT */
  budgetType: string | null;
  subjectId: string | null;
  /** 渠道账户键：GOOGLE_ADS_ACCOUNT / META_ADS_ACCOUNT */
  subjectKey: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  amount: string | null;
  usedAmount: string | null;
  remainAmount: string | null;
  currency: string | null;
  /** ACTIVE / PAUSED */
  status: string | null;
  version: string | null;
};

/** BUD-001 列表信封（{total, items}，无 page/pageSize）。 */
type CorporateBudgetListData = {
  total: string;
  items: CorporateBudgetItem[];
};

/** BUD-002 新建预算 body（金额 decimal string；period 字符串透传不本地换算时区）。 */
type CorporateBudgetCreateBody = {
  budgetType: string;
  parentBudgetId?: string;
  subjectId?: string;
  subjectKey?: string;
  periodStart: string;
  periodEnd: string;
  amount: string;
  currency?: string;
};

/** BUD-003 状态变更 body（枚举名）。 */
type CorporateBudgetStatusBody = {
  status: 'ACTIVE' | 'PAUSED';
};

/** BUD-001 预算树/列表。 */
export async function listCorporateBudgets(
  corpId: string,
  query: CorporateBudgetListQuery = {},
): Promise<ApiResult<CorporateBudgetListData>> {
  const qs = buildBudgetsQuery(query);
  return merchantGet<CorporateBudgetListData>(
    `/corporate/${encodeURIComponent(corpId)}/budgets${qs ? `?${qs}` : ''}`,
    () => ({ total: '0', items: [] }),
  );
}

/** BUD-002 新建预算（Idempotency-Key 恒注入；4510 周期重复 / 4507 超总预算剩余）。 */
export async function createCorporateBudget(
  corpId: string,
  body: CorporateBudgetCreateBody,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateBudgetItem>> {
  return corporateWrite<CorporateBudgetItem>(
    'POST',
    `/corporate/${encodeURIComponent(corpId)}/budgets`,
    body,
    opts,
  );
}

/** BUD-002 额度调整（If-Match 乐观锁必传，冲突 1008）。 */
export async function adjustCorporateBudget(
  budgetId: string,
  amount: string,
  ifMatch: string,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateBudgetItem>> {
  return corporateWrite<CorporateBudgetItem>(
    'PUT',
    `/corporate/budgets/${encodeURIComponent(budgetId)}`,
    { amount },
    { ...opts, ifMatch },
  );
}

/** BUD-003 暂停/恢复（PATCH + X-Confirm: true 二次确认，经 corporateWrite 头映射）。 */
export async function changeCorporateBudgetStatus(
  budgetId: string,
  body: CorporateBudgetStatusBody,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateBudgetItem>> {
  const requestOpts = {
    idempotencyKey: opts.idempotencyKey,
    headers: {
      ...buildCorporateWriteHeaders(opts),
      ...(opts.confirm ? {} : { 'X-Confirm': 'true' }),
    },
  };
  return merchantPatch<CorporateBudgetItem>(
    `/corporate/budgets/${encodeURIComponent(budgetId)}/status`,
    body,
    () => {
      throw new Error('mock_unsupported: USE_MOCK=false');
    },
    requestOpts,
  );
}

// ================= BIL 域（BIL-001/002；PAGE-013） =================

/** BIL-001 周期账单项（CorporateBillItemView 九字段全集）。 */
export type CorporateBillItem = {
  billNo: string;
  /** 周期类型（CorpBillPeriodType 枚举名） */
  periodType: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** DEPARTMENT / MEMBER / PROJECT / CHANNEL_ACCOUNT */
  dimension: string | null;
  /** BIGINT string（部门/成员维度） */
  dimensionValue: string | null;
  /** PROJECT/CHANNEL_ACCOUNT 字符串键（aggregate_json） */
  dimensionKey: string | null;
  totalAmount: string | null;
  currency: string | null;
  /** GENERATED / CONFIRMED */
  status: string | null;
};

/** BIL-001 列表信封。 */
type CorporateBillListData = {
  total: string;
  items: CorporateBillItem[];
};

/** BIL-002 手动补生成 body（dimension 缺省由后端兜底）。 */
type CorporateBillGenerateBody = {
  periodStart?: string;
  periodEnd?: string;
  dimension?: string;
};

/** BIL-001 周期账单列表。 */
export async function listCorporateBills(
  corpId: string,
  query: CorporateBillListQuery = {},
): Promise<ApiResult<CorporateBillListData>> {
  const qs = buildBillsQuery(query);
  return merchantGet<CorporateBillListData>(
    `/corporate/${encodeURIComponent(corpId)}/bills${qs ? `?${qs}` : ''}`,
    () => ({ total: '0', items: [] }),
  );
}

/** BIL-002 手动补生成（Idempotency-Key 恒注入；撞段 4510 不覆盖）。 */
export async function generateCorporateBills(
  corpId: string,
  body: CorporateBillGenerateBody,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateBillItem[]>> {
  return corporateWrite<CorporateBillItem[]>(
    'POST',
    `/corporate/${encodeURIComponent(corpId)}/bills/generate`,
    body,
    opts,
  );
}

// ================= EXP 域（EXP-001/002/003；PAGE-013） =================

/** EXP-001 报销单项（CorporateExpenseItemView 全集；invoiceFileIds 仅私有 objectKey）。 */
export type CorporateExpenseItem = {
  expenseId: string;
  expenseNo: string;
  memberId: string | null;
  billId: string | null;
  txIds: string[];
  invoiceFileIds: string[];
  amount: string | null;
  currency: string | null;
  /** SUBMITTED / REVIEWING / REIMBURSED / REJECTED */
  status: string | null;
  reviewerMemberId: string | null;
  reviewedAt: string | null;
  rejectReason: string | null;
  reimburseRef: string | null;
  version: string | null;
};

/** EXP-001 列表信封。 */
type CorporateExpenseListData = {
  total: string;
  items: CorporateExpenseItem[];
};

/** EXP-001 发起报销 body（txIds BIGINT string 数组；金额 decimal string 透传）。 */
type CorporateExpenseCreateBody = {
  billId?: string;
  txIds: string[];
  invoiceFileIds: string[];
  amount: string;
  currency?: string;
};

/** EXP-002 审核决策 body（REJECT reason 必填由页面校验）。 */
type CorporateExpenseDecisionBody = {
  decision: 'APPROVE' | 'REJECT';
  reason?: string;
};

/** EXP-002 决策结果。 */
type CorporateExpenseDecisionData = {
  expenseId: string;
  expenseNo: string;
  status: string | null;
  reimburseRef: string | null;
};

/** EXP-003 invoice 上传结果（私有 objectKey + 哈希）。 */
type CorporateInvoiceUploadData = {
  fileId: string;
  fileHash: string;
};

/** EXP-001 报销单列表。 */
export async function listCorporateExpenses(
  corpId: string,
  query: CorporateExpenseListQuery = {},
): Promise<ApiResult<CorporateExpenseListData>> {
  const qs = buildExpensesQuery(query);
  return merchantGet<CorporateExpenseListData>(
    `/corporate/${encodeURIComponent(corpId)}/expenses${qs ? `?${qs}` : ''}`,
    () => ({ total: '0', items: [] }),
  );
}

/** EXP-001 发起报销（Idempotency-Key 恒注入；须企业成员身份注入场景由后端 X-Corp-Member-Id 承载）。 */
export async function createCorporateExpense(
  corpId: string,
  body: CorporateExpenseCreateBody,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateExpenseItem>> {
  return corporateWrite<CorporateExpenseItem>(
    'POST',
    `/corporate/${encodeURIComponent(corpId)}/expenses`,
    body,
    opts,
  );
}

/** EXP-002 报销审核决策（APPROVE → REIMBURSED 台账入账；REJECT → REJECTED）。 */
export async function decideCorporateExpense(
  expenseId: string,
  body: CorporateExpenseDecisionBody,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateExpenseDecisionData>> {
  return corporateWrite<CorporateExpenseDecisionData>(
    'POST',
    `/corporate/expenses/${encodeURIComponent(expenseId)}/decision`,
    body,
    opts,
  );
}

/**
 * EXP-003 invoice 上传（multipart 字段 file + corpId；jpeg/png/pdf ≤10MB；
 * 走既有 merchantUpload 边界，不自建直传通道；返回私有 objectKey）。
 */
export async function uploadCorporateInvoice(
  corpId: string,
  file: File,
): Promise<ApiResult<CorporateInvoiceUploadData>> {
  const form = new FormData();
  form.append('corpId', corpId);
  form.append('file', file);
  return merchantUpload<CorporateInvoiceUploadData>(
    '/corporate/expenses/files',
    form,
    () => {
      throw new Error('mock_unsupported: USE_MOCK=false');
    },
  );
}

// ================= RSK 域（RSK-001/002；PAGE-014） =================

/** RSK-001 规则项（ruleId/scopeId long → string；mccWhitelist number[]）。 */
export type CorporateRiskRuleItem = {
  ruleId: string;
  /** MCC_WHITELIST / LIMIT_SINGLE / LIMIT_DAILY / LIMIT_MONTHLY */
  ruleType: string | null;
  mccWhitelist: number[];
  limitAmount: string | null;
  limitCurrency: string | null;
  /** GLOBAL / DEPARTMENT / MEMBER / CARD */
  scope: string | null;
  scopeId: string | null;
  /** ACTIVE / DISABLED */
  status: string | null;
  updateTime: string | null;
};

/** RSK-001 分页出参。 */
type CorporateRiskRuleListData = {
  total: string;
  items: CorporateRiskRuleItem[];
  page: number;
  pageSize: number;
};

/** RSK-002 新建规则 body（scopeId 仅非 GLOBAL；渠道账户枚举口径与 BUD subjectKey 无关）。 */
type CorporateRiskRuleCreateBody = {
  ruleType: string;
  mccCodes?: string[];
  limitAmount?: string;
  limitCurrency?: string;
  scope: string;
  scopeId?: string;
};

/** RSK-002 更新规则 body（If-Match 必传）。 */
type CorporateRiskRuleUpdateBody = {
  mccCodes?: string[];
  limitAmount?: string;
  limitCurrency?: string;
};

/** RSK-002 启停 body（枚举名；X-Confirm: true 由 corporateWrite 头映射）。 */
type CorporateRiskRuleStatusBody = {
  status: 'ACTIVE' | 'DISABLED';
};

/** RSK-001 规则列表（ruleType/scope/status 筛选走 TINYINT code）。 */
export async function listCorporateRiskRules(
  corpId: string,
  query: CorporateRiskRuleListQuery = {},
): Promise<ApiResult<CorporateRiskRuleListData>> {
  const qs = buildRiskRulesQuery(query);
  return merchantGet<CorporateRiskRuleListData>(
    `/corporate/${encodeURIComponent(corpId)}/risk-rules${qs ? `?${qs}` : ''}`,
    () => ({ total: '0', items: [], page: 1, pageSize: 20 }),
  );
}

/** RSK-002 新建规则（Idempotency-Key 恒注入；4512 宽于父级 / 4516 重复规则）。 */
export async function createCorporateRiskRule(
  corpId: string,
  body: CorporateRiskRuleCreateBody,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateRiskRuleItem>> {
  return corporateWrite<CorporateRiskRuleItem>(
    'POST',
    `/corporate/${encodeURIComponent(corpId)}/risk-rules`,
    body,
    opts,
  );
}

/** RSK-002 更新规则（If-Match 乐观锁必传，冲突 1008）。 */
export async function updateCorporateRiskRule(
  ruleId: string,
  body: CorporateRiskRuleUpdateBody,
  ifMatch: string,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateRiskRuleItem>> {
  return corporateWrite<CorporateRiskRuleItem>(
    'PUT',
    `/corporate/risk-rules/${encodeURIComponent(ruleId)}`,
    body,
    { ...opts, ifMatch },
  );
}

/** RSK-002 启停（PATCH + X-Confirm: true 必填；Idempotency-Key 恒注入）。 */
export async function changeCorporateRiskRuleStatus(
  ruleId: string,
  body: CorporateRiskRuleStatusBody,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateRiskRuleItem>> {
  const requestOpts = {
    idempotencyKey: opts.idempotencyKey,
    headers: {
      ...buildCorporateWriteHeaders(opts),
      ...(opts.confirm ? {} : { 'X-Confirm': 'true' }),
    },
  };
  return merchantPatch<CorporateRiskRuleItem>(
    `/corporate/risk-rules/${encodeURIComponent(ruleId)}/status`,
    body,
    () => {
      throw new Error('mock_unsupported: USE_MOCK=false');
    },
    requestOpts,
  );
}

// ================= MBR-002 成员列表 / 邀请（现网 GET/POST /corporate/{corpId}/members） =================

/** 掩码联系对（出参仅掩码，禁完整 email/phone）。 */
type CorporateMemberContactMask = {
  email: string | null;
  phone: string | null;
};

/** MBR-002 成员列表行（CorporateMemberItemView）。 */
export type CorporateMemberItem = {
  memberId: string;
  fullName: string;
  departmentId: string | null;
  role: string | null;
  status: string | null;
  cardCount: number | string;
  contactMask: CorporateMemberContactMask | null;
  version: string | null;
};

type CorporateMemberListData = {
  total: string | number;
  items: CorporateMemberItem[];
  page: number;
  pageSize: number;
};

type CorporateMemberInviteItem = {
  fullName: string;
  email?: string;
  phone?: string;
  departmentId?: string;
  role?: string;
};

type CorporateMemberInviteFailure = {
  index: number;
  fullName?: string | null;
  code?: string | null;
  message?: string | null;
};

export type CorporateMemberInviteResult = {
  successCount: number;
  skippedCount: number;
  failed: CorporateMemberInviteFailure[];
  inviteUrls?: string[] | null;
};

/** MBR-002 GET 成员列表。 */
export async function listCorporateMembers(
  corpId: string,
  query: CorporateMemberListQuery = {},
): Promise<ApiResult<CorporateMemberListData>> {
  const qs = buildMembersQuery(query);
  return merchantGet<CorporateMemberListData>(
    `/corporate/${encodeURIComponent(corpId)}/members${qs ? `?${qs}` : ''}`,
    () => ({ total: '0', items: [], page: 1, pageSize: 20 }),
  );
}

/**
 * MBR-002 POST 邀请（items 与 csvFileId 二选一；Idempotency-Key 由 client 注入）。
 * 邀请 URL 仅本次响应返回，页面一次性展示。
 */
export async function inviteCorporateMembers(
  corpId: string,
  body: { items?: CorporateMemberInviteItem[]; csvFileId?: string },
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateMemberInviteResult>> {
  return corporateWrite<CorporateMemberInviteResult>(
    'POST',
    `/corporate/${encodeURIComponent(corpId)}/members`,
    body,
    opts,
  );
}

// ================= CARD-001/002 企业卡列表 / 开卡 =================

/** CARD-001 企业卡列表行（仅 panMask，无明文 PAN/CVV）。 */
export type CorporateCardItem = {
  corpCardId: string;
  cardId: string;
  panMask: string | null;
  memberName: string | null;
  departmentName: string | null;
  projectKey: string | null;
  usageLabel: string | null;
  budgetName: string | null;
  status: string | null;
  limitSingle: string | null;
  limitDaily: string | null;
  limitMonthly: string | null;
};

type CorporateCardListData = {
  total: string | number;
  items: CorporateCardItem[];
};

export type CorporateCardIssueBody = {
  dimension: string;
  memberId?: string;
  departmentId?: string;
  projectKey?: string;
  productCode: string;
  cardType: 'VIRTUAL' | 'PHYSICAL';
  limitSingle: string;
  limitDaily: string;
  limitMonthly: string;
  budgetId: string;
  usageLabel?: string;
};

type CorporateCardIssueResult = {
  corpCardId?: string;
  cardId?: string;
  status?: string;
  panMask?: string;
  successCount?: number;
  failed?: { memberId?: string; code?: string; message?: string }[];
  cards?: { corpCardId: string; cardId: string; status: string; panMask: string }[];
};

/** CARD-001 GET 企业卡列表。 */
export async function listCorporateCards(
  corpId: string,
  query: CorporateCardListQuery = {},
): Promise<ApiResult<CorporateCardListData>> {
  const qs = buildCardsQuery(query);
  return merchantGet<CorporateCardListData>(
    `/corporate/${encodeURIComponent(corpId)}/cards${qs ? `?${qs}` : ''}`,
    () => ({ total: '0', items: [] }),
  );
}

/** CARD-002 POST 企业开卡（单卡或部门批量；金额 decimal string）。 */
export async function issueCorporateCard(
  corpId: string,
  body: CorporateCardIssueBody,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateCardIssueResult>> {
  return corporateWrite<CorporateCardIssueResult>(
    'POST',
    `/corporate/${encodeURIComponent(corpId)}/cards`,
    body,
    opts,
  );
}

// ================= TPL-002 套用场景模板 =================

type CorporateTemplateApplyResult = {
  corpId: string;
  templateCode: string;
  applied: boolean;
};

/** TPL-002 POST /corporate/{corpId}/templates/{templateCode}/apply（广告投放 = AD_SPEND_ADS）。 */
export async function applyCorporateTemplate(
  corpId: string,
  templateCode: string,
  opts: CorporateWriteOpts = {},
): Promise<ApiResult<CorporateTemplateApplyResult>> {
  return corporateWrite<CorporateTemplateApplyResult>(
    'POST',
    `/corporate/${encodeURIComponent(corpId)}/templates/${encodeURIComponent(templateCode)}/apply`,
    {},
    opts,
  );
}
